import type { Discrepancy, Manuscript, ManuscriptToken } from "../api/types";
import { formatTime } from "./format";
import { categoryOf } from "./reportStore";

/**
 * A concise note built from the pickup's own snapshot (used when the live
 * report commentary isn't loaded) — so the card's demoted note is always present.
 */
export function fallbackNote(
  flagType: string,
  script: string,
  heard: string,
  start?: number | null,
  end?: number | null,
): string {
  const cat = categoryOf(flagType || "");
  const s = (script || "").trim();
  const h = (heard || "").trim();
  if (cat === "misread") return s && h ? `“${s}” read as “${h}”` : s ? `“${s}” misread` : "";
  if (cat === "missing") return s ? `“${s}” missing from the audio` : "";
  if (cat === "added") return h ? `“${h}” added — not in the manuscript` : "";
  if (cat === "pause") {
    const d = start != null && end != null && end > start ? end - start : null;
    return d ? `${d.toFixed(1)}s of silence` : "";
  }
  return "";
}

/** Which timeline a pickup's timecode is measured against. */
export type TimecodeBasis = "chapter" | "book";

/**
 * Format a pickup's timecode on the chosen basis: "chapter" = from the start of
 * its chapter/take, "book" = from the start of the whole audiobook (chapter
 * offset added).
 */
export function formatTimecode(
  start: number | null | undefined,
  offset: number | undefined,
  basis: TimecodeBasis,
): string {
  if (start == null) return "";
  return formatTime(basis === "book" ? start + (offset || 0) : start);
}

// Default context = ~3× the engine's stored ~6 words → ~18 words each side.
export const BASE_WORDS = 18;
// "Show more" reveals ~3 more manuscript lines each side per click (~12 words/line).
export const STEP_WORDS = 36;
// When the window ends on the last token, play a little past it so the final
// word isn't clipped.
const TAIL = 1.2;

export interface CtxToken {
  text: string;
  audio_time?: number | null;
  // The original manuscript characters that follow this token up to the next
  // (spaces, commas, periods, …). When present, the excerpt is reconstructed
  // with the manuscript's exact punctuation; otherwise tokens join with a space.
  sep?: string;
}

/**
 * Flatten a chapter manuscript into context tokens carrying their original
 * trailing separator, so the owner card can render the exact manuscript text
 * (punctuation, capitalisation) — same shape the share window already bundles.
 */
export function manuscriptTokensWithSep(manuscript: Manuscript): CtxToken[] {
  const tokens = manuscript.tokens;
  const paraText = new Map<string, string>();
  for (const pg of manuscript.pages) {
    pg.paragraphs.forEach((para, j) => paraText.set(`${pg.page_number}:${j}`, para.text));
  }
  const textOf = (t: ManuscriptToken) => paraText.get(`${t.page}:${t.para_index}`) ?? "";
  const out: CtxToken[] = new Array(tokens.length);
  for (let i = 0; i < tokens.length; i++) {
    const a = tokens[i];
    const b = tokens[i + 1];
    let sep = "";
    if (b) {
      const pt = textOf(a);
      const ae = a.char_span_in_para?.[1] ?? 0;
      if (b.page === a.page && b.para_index === a.para_index) {
        sep = pt.slice(ae, b.char_span_in_para?.[0] ?? ae) || " ";
      } else {
        sep = (pt.slice(ae) || "") + " "; // end of paragraph: trailing punctuation + break
      }
    }
    out[i] = { text: a.text, audio_time: a.audio_time, sep };
  }
  return out;
}

export interface Ctx {
  before: string;
  flag: string;
  after: string;
  audioStart: number | null;
  audioEnd: number | null;
  hasMore: boolean;
}

/**
 * The flag's span (first/last token index) within a token array. For "added"
 * flags the span is empty (`last = first - 1`) at the insertion caret. Returns
 * null when there's no manuscript anchor (caller falls back to the snapshot).
 * `manuscript_token_indices` index the SAME token array you pass to buildContext.
 */
export function flagSpan(d: Discrepancy): { first: number; last: number } | null {
  const idxs = d.manuscript_token_indices ?? [];
  if (idxs.length) return { first: Math.min(...idxs), last: Math.max(...idxs) };
  const anchor = d.insert_before_index;
  if (anchor == null) return null;
  return { first: anchor, last: anchor - 1 };
}

// Stored anchors (an imported marker's or a selection flag's token indices) are
// positions in the chapter's words AS THEY WERE. Proofing the chapter again can
// re-cut those words: on "Dead to Us" four left the top of a chapter, so every
// marker in it pointed four words late and the last pointed past the end, which
// blanked the Pickups Package. The pickup also keeps its words (script text and
// the manuscript either side), so an anchor is used only where those words are.
// Mirror of backend `modules/proofing/anchors.py`: keep the two in step.
const EDGE_WORDS = 2; // words of stored context compared on each side
const EDGE_TOKENS = 4; // tokens those words may take up (hyphens, dashes)
const normedWords = new WeakMap<ReadonlyArray<{ text: string }>, string[]>();

function normWord(s: string): string {
  return (s || "").normalize("NFKD").toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
}

/**
 * Where a stored anchor's words are in `tokens` now: at its stored position when
 * they still match there, else the nearest position where they do, else null
 * (the card shows the excerpt stored with the pickup).
 */
export function settleSpan(
  tokens: ReadonlyArray<{ text: string }>,
  indices: number[] | null | undefined,
  snap: { script?: string | null; before?: string | null; after?: string | null },
): { first: number; last: number } | null {
  if (!indices?.length || !tokens.length) return null;
  let w = normedWords.get(tokens);
  if (!w) {
    w = tokens.map((t) => normWord(t.text));
    normedWords.set(tokens, w);
  }
  const words = w;
  const n = words.length;
  const first = Math.min(...indices);
  const last = Math.max(...indices);
  const width = last - first;
  const s = normWord(snap.script ?? "");
  const b = normWord((snap.before ?? "").split(/\s+/).filter(Boolean).slice(-EDGE_WORDS).join(""));
  const a = normWord((snap.after ?? "").split(/\s+/).filter(Boolean).slice(0, EDGE_WORDS).join(""));
  // Two of the three when there are three to go on: a quote that never was in
  // the manuscript still settles by the words either side, and a common word
  // ("and") can't settle on the wrong "and".
  const need = Math.min(2, (s ? 1 : 0) + (b ? 1 : 0) + (a ? 1 : 0));
  const fits = (f: number) => {
    if (f < 0 || f + width >= n) return false;
    if (need === 0) return true;
    let score = 0;
    if (s && words.slice(f, f + width + 1).join("") === s) score++;
    if (b && words.slice(Math.max(0, f - EDGE_TOKENS), f).join("").endsWith(b)) score++;
    if (a && words.slice(f + width + 1, f + width + 1 + EDGE_TOKENS).join("").startsWith(a)) score++;
    return score >= need;
  };
  if (fits(first)) return { first, last };
  if (need === 0) return null;
  // Nearest first: a re-cut shifts every anchor by the same few words.
  for (let d = 1; d < n; d++) {
    if (fits(first - d)) return { first: first - d, last: last - d };
    if (fits(first + d)) return { first: first + d, last: last + d };
    if (first - d < 0 && first + d >= n) break;
  }
  return null;
}

/**
 * Build a windowed, readable excerpt around a flag — `wordsEachSide` tokens
 * before/after the flag's span, highlighted in the middle — plus the audio span
 * of the *displayed* window (first shown token → just past the last) so a player
 * auditions exactly the text on screen. Source-agnostic: feed it the full
 * manuscript tokens + global indices (owner, unbounded "show more") or a bounded
 * window slice + local indices (public share, bounded). Returns null for an
 * empty token array.
 */
export function buildContext(
  tokens: CtxToken[],
  first: number,
  last: number,
  heardText: string,
  wordsEachSide: number,
): Ctx | null {
  if (!tokens?.length) return null;
  // A span outside the tokens would read past the end below, and one card
  // throwing blanks the whole page. The caller shows its stored excerpt instead.
  if (first < 0 || first > tokens.length || last >= tokens.length || last < first - 1) return null;
  const hasFlag = last >= first;
  const beforeStart = Math.max(0, first - wordsEachSide);
  const afterEnd = Math.min(tokens.length - 1, last + wordsEachSide);
  const sepOf = (i: number) => tokens[i].sep ?? " ";

  // Reconstruct each region using the original inter-token characters, so the
  // flagged token's surrounding punctuation lands in `before`/`after`, not in
  // the highlight. (Falls back to single spaces when `sep` isn't supplied.)
  let before = "";
  for (let i = beforeStart; i < first; i++) before += tokens[i].text + sepOf(i);
  let flag = "";
  if (hasFlag) {
    for (let i = first; i <= last; i++) flag += tokens[i].text + (i < last ? sepOf(i) : "");
  } else {
    flag = heardText || "";
  }
  let after = hasFlag ? sepOf(last) : " ";
  for (let i = last + 1; i <= afterEnd; i++) after += tokens[i].text + (i < afterEnd ? sepOf(i) : "");

  const audioStart = tokens[beforeStart]?.audio_time ?? null;
  const nextAfter = tokens[afterEnd + 1]?.audio_time;
  const lastShown = tokens[afterEnd]?.audio_time;
  const audioEnd =
    nextAfter != null ? nextAfter : lastShown != null ? lastShown + TAIL : null;

  return {
    before: before.replace(/^\s+/, ""),
    flag,
    after: after.replace(/\s+$/, ""),
    audioStart,
    audioEnd,
    hasMore: beforeStart > 0 || afterEnd < tokens.length - 1,
  };
}
